import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion, useMotionValue, useSpring } from 'framer-motion';
import './HeroSection.css';

const ROTATING_TAGS = [
  'RAG systems',
  'AI agents',
  'Full-stack AI products',
  'Python platform engineering'
];

const HeroSection = () => {
  const [tagIndex, setTagIndex] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [phase, setPhase] = useState('typing'); // typing | holding | deleting

  // Scroll-linked motion: the copy lifts and fades, the code window drifts a
  // little slower, and the background loop zooms in and dims as you leave the hero.
  const heroRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const visualY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const mediaScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const mediaOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);
  const motionStyle = (style) => (reduceMotion ? undefined : style);

  // Pointer tilt on the code window: a few degrees, spring-smoothed.
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const springTiltX = useSpring(tiltX, { stiffness: 110, damping: 18 });
  const springTiltY = useSpring(tiltY, { stiffness: 110, damping: 18 });

  const handlePointerMove = (event) => {
    if (reduceMotion || !heroRef.current || event.pointerType !== 'mouse') return;
    const rect = heroRef.current.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    tiltX.set(-py * 6);
    tiltY.set(px * 8);
  };

  const handlePointerLeave = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  useEffect(() => {
    const target = ROTATING_TAGS[tagIndex];
    let timer;

    if (phase === 'typing') {
      if (typedText.length < target.length) {
        timer = setTimeout(() => {
          setTypedText(target.slice(0, typedText.length + 1));
        }, 60);
      } else {
        timer = setTimeout(() => setPhase('holding'), 1500);
      }
    } else if (phase === 'holding') {
      timer = setTimeout(() => setPhase('deleting'), 800);
    } else if (phase === 'deleting') {
      if (typedText.length > 0) {
        timer = setTimeout(() => {
          setTypedText(target.slice(0, typedText.length - 1));
        }, 35);
      } else {
        setTagIndex((tagIndex + 1) % ROTATING_TAGS.length);
        setPhase('typing');
      }
    }

    return () => clearTimeout(timer);
  }, [typedText, phase, tagIndex]);

  const scrollToContact = (e) => {
    e.preventDefault();
    const services = document.getElementById('services');
    if (services) {
      services.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="hero-section" ref={heroRef} onPointerMove={handlePointerMove} onPointerLeave={handlePointerLeave}>
      <div className="hero-media" aria-hidden="true">
        <motion.div className="hero-media-frame" style={motionStyle({ scale: mediaScale, opacity: mediaOpacity })}>
          {reduceMotion ? (
            <img className="hero-media-video" src="/media/hero-poster.jpg" alt="" />
          ) : (
            <video
              className="hero-media-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster="/media/hero-poster.jpg"
            >
              <source src="/media/hero-loop.webm" type="video/webm" />
              <source src="/media/hero-loop.mp4" type="video/mp4" />
            </video>
          )}
        </motion.div>
        <div className="hero-media-shade"></div>
      </div>

      <div className="hero-container">
        <motion.div className="hero-content" style={motionStyle({ y: contentY, opacity: contentOpacity })}>
          <div className="hero-badge">
            <span className="badge-icon">▲</span>
            Full-stack AI Product Studio
          </div>

          <h1 className="hero-title">
            We ship production-grade AI products on the modern <span className="logo-highlight">full-stack</span>.
          </h1>

          <p className="hero-subtitle">
            Senior-led full-stack engineering for production AI products. Backend, frontend, AI, cloud, and DevOps — shipped end to end with clear timelines.
          </p>

          <div className="hero-typed-row" aria-live="polite">
            <span className="hero-typed-label">We build:</span>
            <span className="logo-highlight hero-typed-text">{typedText}</span>
            <span className="cursor">|</span>
          </div>

          <div className="hero-actions">
            <a href="#services" onClick={scrollToContact} className="btn-primary hero-action-btn" aria-label="See our work and services">
              <span className="action-text">See our work</span>
              <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            </a>

            <a href="mailto:info@pysolutions.dev" className="btn-secondary hero-action-btn" aria-label="Book a 30-minute call">
              <span className="action-text">Book a 30-min call</span>
              <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 11H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2z" />
              </svg>
            </a>
          </div>

          <div className="hero-features">
            <div className="feature-item">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span>Senior-led, AI-accelerated delivery</span>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span>Reference stack, not slides</span>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <span>Quantified outcomes</span>
            </div>
          </div>
        </motion.div>

        <motion.div className="hero-visual" style={motionStyle({ y: visualY, rotateX: springTiltX, rotateY: springTiltY })}>
          <div className="code-window">
            <div className="window-header">
              <div className="window-controls">
                <span className="control red"></span>
                <span className="control yellow"></span>
                <span className="control green"></span>
              </div>
              <div className="window-title">main.py</div>
            </div>
            <div className="code-content">
              <div className="code-line">
                <span className="line-number">1</span>
                <span className="code-text">
                  <span className="keyword">from</span> <span className="module">fastapi</span> <span className="keyword">import</span> <span className="class">FastAPI</span>
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">2</span>
                <span className="code-text">
                  <span className="keyword">from</span> <span className="module">pysolutions.expertise</span> <span className="keyword">import</span> <span className="class">PySolutions</span>
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">3</span>
                <span className="code-text"></span>
              </div>
              <div className="code-line">
                <span className="line-number">4</span>
                <span className="code-text">
                  <span className="variable">app</span> = <span className="class">FastAPI</span>()
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">5</span>
                <span className="code-text">
                  <span className="variable">solution_generator</span> = <span className="class">PySolutions</span>(<span className="string">"AI"</span>, <span className="string">"full-stack"</span>, <span className="string">"cloud"</span>)
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">6</span>
                <span className="code-text"></span>
              </div>
              <div className="code-line">
                <span className="line-number">7</span>
                <span className="code-text">
                  <span className="decorator">@app.get</span>(<span className="string">"/solutions"</span>)
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">8</span>
                <span className="code-text">
                  <span className="keyword">def</span> <span className="function">get_solutions</span>(<span className="variable">client_problem</span>: <span className="type">str</span>):
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">9</span>
                <span className="code-text">
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="variable">solution</span> = <span className="variable">solution_generator</span>.<span className="function">solve_problem</span>(<span className="variable">client_problem</span>)
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">10</span>
                <span className="code-text"></span>
              </div>
              <div className="code-line">
                <span className="line-number">11</span>
                <span className="code-text">
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="keyword">return</span> {'{'}
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">12</span>
                <span className="code-text">
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="string">"client_problem"</span>: <span className="variable">client_problem</span>,
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">13</span>
                <span className="code-text">
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="string">"solution"</span>: <span className="variable">solution</span>,
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">14</span>
                <span className="code-text">
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="string">"status"</span>: <span className="string">"Problem solved!"</span>
                </span>
              </div>
              <div className="code-line">
                <span className="line-number">15</span>
                <span className="code-text">
                  &nbsp;&nbsp;&nbsp;&nbsp;{'}'}
                </span>
              </div>
            </div>
          </div>

          <div className="floating-elements">
            <div className="tech-badge tech-python">Python</div>
            <div className="tech-badge tech-ai">AI/ML</div>
            <div className="tech-badge tech-cloud">Cloud</div>
            <div className="tech-badge tech-api">FastAPI</div>
            <div className="tech-badge tech-iot">Next.js</div>
            <div className="tech-badge tech-docker">DevOps</div>
          </div>
        </motion.div>
      </div>

      <div className="scroll-indicator">
        <div className="scroll-line"></div>
        <div className="scroll-text">Scroll to explore</div>
      </div>
    </section>
  );
};

export default HeroSection;
